import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaArrowLeft, FaStar } from 'react-icons/fa';
import publicData from '../../../data/publicData.json';

const FeaturedCaseStudies = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  const sliderRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [activeCategory, setActiveCategory] = useState(0);

  const { caseStudies } = publicData;
  const { categories } = caseStudies;

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const handleScroll = () => {
      const maxScroll = slider.scrollWidth - slider.clientWidth;
      const currentScroll = slider.scrollLeft;
      
      setShowLeftArrow(currentScroll > 0);
      setShowRightArrow(currentScroll < maxScroll - 10);
      
      // Update current slide based on scroll position
      const slideWidth = slider.clientWidth;
      const newSlide = Math.round(currentScroll / slideWidth);
      if (newSlide !== currentSlide) {
        setCurrentSlide(newSlide);
      }
    };

    slider.addEventListener('scroll', handleScroll);
    return () => slider.removeEventListener('scroll', handleScroll);
  }, [currentSlide]);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      if (sliderRef.current) {
        const maxScroll = sliderRef.current.scrollWidth - sliderRef.current.clientWidth;
        const currentScroll = sliderRef.current.scrollLeft;

        if (currentScroll >= maxScroll) {
          sliderRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          sliderRef.current.scrollBy({ left: sliderRef.current.clientWidth, behavior: 'smooth' });
        }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const scrollToSlide = (direction) => {
    if (!sliderRef.current) return;

    const slider = sliderRef.current;
    const slideWidth = slider.clientWidth;
    const targetScroll = direction === 'left' 
      ? slider.scrollLeft - slideWidth
      : slider.scrollLeft + slideWidth;

    slider.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });

    // Pause auto-play temporarily when manually navigating
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <section 
      id="featured-case-studies" 
      className={`py-20 ${
        isDark 
          ? 'bg-gradient-to-r from-gray-900 to-black'
          : 'bg-gradient-to-r from-gray-100 to-white'
      }`}
    >
      <motion.div
        className="container mx-auto px-4"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
      >
        <motion.div 
          className="text-center max-w-4xl mx-auto mb-16"
          variants={itemVariants}
        >
          <h2 className={`text-4xl font-bold mb-6 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            {caseStudies.title}
          </h2>
          <p className={`text-lg ${
            isDark ? 'text-gray-300' : 'text-gray-600'
          }`}>
            {caseStudies.description}
          </p>
        </motion.div>

        {/* Category Navigation */}
        <motion.div 
          className="flex justify-center gap-4 mb-8"
          variants={itemVariants}
        >
          {categories.map((category, index) => (
            <button
              key={category.title}
              onClick={() => {
                setActiveCategory(index);
                setCurrentSlide(0);
                if (sliderRef.current) {
                  sliderRef.current.scrollTo({ left: 0, behavior: 'smooth' });
                }
              }}
              className={`px-4 py-2 rounded-lg transition-all ${
                activeCategory === index
                  ? isDark 
                    ? 'bg-blue-600 text-white'
                    : 'bg-blue-500 text-white'
                  : isDark
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category.title}
            </button>
          ))}
        </motion.div>

        <div className="relative">
          {/* Navigation Arrows */}
          <button
            onClick={() => scrollToSlide('left')}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 p-4 rounded-full transition-all duration-300 ${
              showLeftArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
            } ${
              isDark 
                ? 'bg-gray-800 text-white hover:bg-gray-700'
                : 'bg-white text-gray-800 hover:bg-gray-100'
            }`}
          >
            <FaArrowLeft size={24} />
          </button>

          <button
            onClick={() => scrollToSlide('right')}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 p-4 rounded-full transition-all duration-300 ${
              showRightArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
            } ${
              isDark 
                ? 'bg-gray-800 text-white hover:bg-gray-700'
                : 'bg-white text-gray-800 hover:bg-gray-100'
            }`}
          >
            <FaArrowRight size={24} />
          </button>

          {/* Case Studies Slider */}
          <motion.div
            ref={sliderRef}
            className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory"
            variants={containerVariants}
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {categories[activeCategory].studies.map((study, index) => (
              <motion.div
                key={study.id}
                className="flex-none w-full lg:w-1/2 xl:w-1/3 p-4 snap-start"
                variants={itemVariants}
              >
                <div 
                  className={`h-full rounded-xl overflow-hidden transition-all duration-300 transform hover:scale-[1.02] ${
                    isDark
                      ? 'bg-gray-800/50 hover:bg-gray-800/70'
                      : 'bg-white hover:bg-gray-50'
                  } backdrop-blur-sm shadow-lg`}
                >
                  {/* Case Study Content */}
                  <div className="p-6">
                    <div className={`text-sm font-semibold mb-3 ${
                      isDark ? 'text-blue-400' : 'text-blue-600'
                    }`}>
                      {categories[activeCategory].subtitle}
                    </div>
                    <h3 className={`text-xl font-bold mb-3 ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      {study.title}
                    </h3>
                    <p className={`mb-4 ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {study.solution}
                    </p>

                    {/* Results */}
                    <div className="grid grid-cols-1 gap-2 mb-6">
                      {study.results.map((result, idx) => (
                        <div 
                          key={idx}
                          className={`text-sm ${
                            isDark ? 'text-gray-300' : 'text-gray-600'
                          }`}
                        >
                          ✓ {result}
                        </div>
                      ))}
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {study.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            isDark 
                              ? 'bg-gray-700 text-gray-300'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Testimonial */}
                    <div className={`mb-6 p-4 rounded-lg ${
                      isDark ? 'bg-gray-700/50' : 'bg-gray-50'
                    }`}>
                      <div className="flex items-center mb-2">
                        <FaStar className="text-yellow-400 mr-1" />
                        <FaStar className="text-yellow-400 mr-1" />
                        <FaStar className="text-yellow-400 mr-1" />
                        <FaStar className="text-yellow-400 mr-1" />
                        <FaStar className="text-yellow-400" />
                      </div>
                      <p className={`italic text-sm mb-2 ${
                        isDark ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        "{study.testimonial}"
                      </p>
                      <p className={`text-sm font-semibold ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {study.testimonialAuthor}
                      </p>
                    </div>

                    {/* CTA Button */}
                    <Link
                      to={`/case-studies/${study.id}`}
                      className={`inline-flex items-center px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 ${
                        isDark 
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-blue-500 text-white hover:bg-blue-600'
                      } transform hover:scale-105 active:scale-95`}
                    >
                      View Case Study
                      <FaArrowRight className="ml-2" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Slide Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {Array.from({ length: categories[activeCategory].studies.length }).map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  if (sliderRef.current) {
                    sliderRef.current.scrollTo({
                      left: index * sliderRef.current.clientWidth,
                      behavior: 'smooth'
                    });
                    setCurrentSlide(index);
                  }
                }}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  currentSlide === index 
                    ? isDark 
                      ? 'bg-blue-500 w-4' 
                      : 'bg-blue-600 w-4'
                    : isDark
                      ? 'bg-gray-600'
                      : 'bg-gray-300'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div 
          className="text-center mt-16"
          variants={itemVariants}
        >
          <Link
            to={caseStudies.cta.secondary.link}
            className={`inline-flex items-center px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 ${
              isDark 
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            } transform hover:scale-105 active:scale-95`}
          >
            {caseStudies.cta.secondary.text}
            <FaArrowRight className="ml-2" />
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default FeaturedCaseStudies; 